// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { AzureLogger, setLogLevel } from '@azure/logger'; // redirect log output
import { IApplicationInsights } from '@microsoft/applicationinsights-web';
import {
  createAppInsightsConnector,
  connectAzureCommunicationLog
} from 'error-telemetry/AzureCommunicationLogConnector';
import { getDefaultAppInsightsConnectionString } from 'utils/appInsightsUtils';
import { getCallIdHistoryFromLocalStorage } from './callIdTracking';

export type FeedbackLogs = {
  callIdHistory: string[];
  azureLogs: string[];
  consoleErrors: string[];
};

const azure_logs: string[] = [];
const console_errors: string[] = [];

// Log line character limit to prevent enormous logs
const LOG_LINE_CHARACTER_LIMIT = 1000;

export const initLogger = async (functionsAppToken: string): Promise<IApplicationInsights> => {
  AzureLogger.log = (...args): void => {
    if (!args || args.length === 0) {
      return;
    }

    const trimmedLogLines = args.map((arg: string) => ('' + arg).slice(0, LOG_LINE_CHARACTER_LIMIT));
    azure_logs.push(...trimmedLogLines);

    // Log calling logs to console
    if (args[0].startsWith('azure:ACS:info')) {
      console.info(...args);
    } else if (args[0].startsWith('azure:ACS:verbose')) {
      console.debug(...args);
    } else if (args[0].startsWith('azure:ACS:warning')) {
      console.warn(...args);
    } else if (args[0].startsWith('azure:ACS:error')) {
      console.error(...args);
    } else {
      console.log(...args);
    }
  };

  setLogLevel('verbose');

  const connectionString = await getDefaultAppInsightsConnectionString(functionsAppToken);
  const connector = createAppInsightsConnector(connectionString);
  connectAzureCommunicationLog(connector);
  return connector.appInsights;
};

export const getLogs = (): FeedbackLogs => {
  // Prepend last know callIds to logs
  const callIdHistory = getCallIdHistoryFromLocalStorage();
  const callIdHistoryStrings = callIdHistory.map(
    (callItem) => `Previously found callId: ${callItem.callId} on ${callItem.date}`
  );
  return {
    callIdHistory: callIdHistoryStrings,
    consoleErrors: console_errors,
    azureLogs: azure_logs
  };
};

/// Track console errors

// Save the original console.error method
const originalConsoleError = console.error;
// Override the console.error method
console.error = function (...args) {
  // Capture the current stack trace
  const errorObj = {};
  Error.captureStackTrace(errorObj, console.error); // Exclude console.error from the stack trace

  // Push the error message and stack trace into the array
  let errorAsString = args.join(' ');
  if ('stack' in errorObj) {
    errorAsString += ' ' + errorObj['stack'];
  }
  errorAsString = errorAsString.slice(0, LOG_LINE_CHARACTER_LIMIT);
  console_errors.push(errorAsString);

  // Call the original console.error with the error
  originalConsoleError.apply(console, args);
};

// Capture unhandled promise rejections
window.addEventListener('unhandledrejection', function (event) {
  const logLine = getLogLineFromUnhandledRejectionEvent(event).slice(0, LOG_LINE_CHARACTER_LIMIT);
  console_errors.push(logLine);
});

const getLogLineFromUnhandledRejectionEvent = (event: PromiseRejectionEvent): string => {
  if (!('reason' in event)) {
    return 'Unknown unhandled rejection';
  }

  let logLine = 'Unhandled Rejection.';
  if ('message' in event.reason) {
    logLine += ' ' + event.reason.message;
  }
  if ('stack' in event.reason) {
    logLine += ' ' + event.reason.stack;
  }
  logLine = logLine.slice(0, LOG_LINE_CHARACTER_LIMIT);

  return logLine;
};
